import React, { createContext, useContext, useEffect, useState } from 'react';
import { setupEventSource } from './api';

// Create context
const EventContext = createContext();

// Event types
export const EVENT_TYPES = {
  QUIZ_START: 'quiz_start',
  QUIZ_END: 'quiz_end',
  QUIZ_UPDATE: 'quiz_update',
  ANNOUNCEMENT: 'announcement',
  LEADERBOARD_UPDATE: 'leaderboard_update',
  CUSTOM: 'custom'
};

// Provider component
export const EventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  const [connected, setConnected] = useState(false);
  const [eventSource, setEventSource] = useState(null);

  // Initialize SSE connection
  useEffect(() => {
    // Only setup if we have a session ticket
    const sessionTicket = localStorage.getItem('sessionTicket');
    if (!sessionTicket) return;

    console.log('Setting up event source in EventContext');
    const source = setupEventSource();
    if (source) {
      // Add event listener to the source
      source.onmessage = handleEvent;
      
      setEventSource(source);
      setConnected(true);

      // Add event listener for custom events
      window.addEventListener('event-received', handleCustomEvent);
    }

    // Return cleanup function
    return () => {
      console.log('Cleaning up event source in EventContext');
      if (source) {
        source.close();
      }
      window.removeEventListener('event-received', handleCustomEvent);
    };
  }, []);

  // Handle incoming events
  const handleEvent = (event) => {
    try {
      const data = JSON.parse(event.data);
      console.log('Event received:', data);

      // Add to events list
      setEvents(prevEvents => [data, ...prevEvents].slice(0, 50)); // Keep last 50 events
      setLastEvent(data);

      // Dispatch custom event for components to listen to
      const customEvent = new CustomEvent('event-received', { detail: data });
      window.dispatchEvent(customEvent);
    } catch (error) {
      console.error('Error handling event:', error);
    }
  };

  // Handle custom events (for testing or manual triggers)
  const handleCustomEvent = (e) => {
    const data = e.detail;
    setEvents(prevEvents => [data, ...prevEvents].slice(0, 50));
    setLastEvent(data);
  };

  // Manually send an event (for testing)
  const sendEvent = (eventData) => {
    const customEvent = new CustomEvent('event-received', { detail: eventData });
    window.dispatchEvent(customEvent);
  };

  // Get events by type
  const getEventsByType = (type) => {
    return events.filter(event => event.type === type);
  };

  // Get last event by type
  const getLastEventByType = (type) => {
    return events.find(event => event.type === type);
  };

  // Context value
  const value = {
    events,
    lastEvent,
    connected,
    getEventsByType,
    getLastEventByType,
    sendEvent
  };

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};

// Custom hook to use the event context
export const useEvents = () => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error('useEvents must be used within an EventProvider');
  }
  return context;
};

export default EventContext;
